/**
 * Navigation handlers extracted from `useForm/index.tsx`.
 *
 * Owns the small handlers that move the form forward/back, manage the
 * URL hash, focus the first input on the first step, and scroll the
 * page to the top of the form. Behavior is preserved verbatim.
 */

import { ActionTypes, FormReducerAction, InitState } from "../types";
import type { DomainForm } from "src/api";
import { findPrevVisibleIndex } from "../utils";

interface UseNavigationParams {
    form: DomainForm;
    state: InitState;
    dispatch: (action: FormReducerAction) => void;
    setWindowStep: (step: number) => void;
    skippedStepIndices: Set<number>;
    instanceId: string;
    isGeneralModalForm?: boolean;
    synchronizedForms?: boolean | undefined;
}

export const useNavigation = ({
    form,
    state,
    dispatch,
    setWindowStep,
    skippedStepIndices,
    instanceId,
    isGeneralModalForm,
    synchronizedForms,
}: UseNavigationParams) => {
    const { currentStep, otp } = state;

    const handlePrevStep = () => {
        const prevVisible = findPrevVisibleIndex(
            currentStep,
            skippedStepIndices,
        );
        dispatch({
            type: ActionTypes.SET_STEP,
            payload: prevVisible,
        });
        if (synchronizedForms) {
            // Notify peer forms about the step change so they can sync.
            window.dispatchEvent(
                new CustomEvent("formStepChanged", {
                    detail: {
                        formId: String(form.id),
                        instanceId,
                        step: prevVisible,
                    },
                }),
            );
        }

        // Only manipulate browser history for non-modal forms.
        if (!isGeneralModalForm) {
            window.history.back();
        }
    };

    const handleWindowBack = () => {
        if (!otp.isResolved && window.location.hash.startsWith("#step-")) {
            const step = parseInt(
                window.location.hash.replace("#step-", ""),
                10,
            );
            const targetIndex = step - 1;
            if (skippedStepIndices.has(targetIndex)) {
                const visibleIndex = findPrevVisibleIndex(
                    targetIndex,
                    skippedStepIndices,
                );
                window.history.replaceState(
                    "",
                    document.title,
                    `#step-${visibleIndex + 1}`,
                );
                setWindowStep(visibleIndex + 1);
            } else {
                setWindowStep(step);
            }
        } else {
            window.history.pushState("", document.title, `#step-1`);
            dispatch({
                type: ActionTypes.SHOW_EXIT_MODAL,
                payload: 1,
            });
        }
    };

    const handleFirstStepFieldFocus = () => {
        const selector =
            "input[type=text], input[type=tel], input[type=number], input[type=date],input[type=email], textarea";

        if (
            document
                .getElementById(`form_${form.id}`)
                ?.querySelectorAll(selector).length
        ) {
            setTimeout(() => {
                const element = document
                    .getElementById(`form_${form.id}`)
                    ?.querySelectorAll(selector);
                if (element && element.length) {
                    (element[0] as HTMLInputElement).focus();
                }
            }, 300);
        }
    };

    const handleScrollFormTop = () => {
        try {
            window.scroll({
                behavior: "smooth",
                top: 0,
            });
        } catch (err) {
            if (err instanceof TypeError) {
                window.scroll(0, 0);
            } else {
                throw err;
            }
        }
    };

    return {
        handlePrevStep,
        handleWindowBack,
        handleFirstStepFieldFocus,
        handleScrollFormTop,
    };
};
